import { Popover, Box, Typography, Chip, Divider, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { STATUS_COLOR } from '../statusMeta';
import type { KpiValue } from '@teamsystem-impact/shared';

interface KpiSourcePopoverProps {
  anchorEl: Element | null;
  open: boolean;
  onClose: () => void;
  kpi: KpiValue & { label?: string };
  campaignName: string;
  onCodeClick?: (code: string) => void;
}

export function KpiSourcePopover({ anchorEl, open, onClose, kpi, campaignName, onCodeClick }: KpiSourcePopoverProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const displayValue =
    kpi.currentValue !== null
      ? new Intl.NumberFormat(locale).format(kpi.currentValue)
      : kpi.textValue || '—';

  const codeClickable = !!onCodeClick;
  const statusLabel = t(`dataCapture.status.${kpi.status}`, { defaultValue: kpi.status });

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: { p: 2.5, width: 320, borderTop: '4px solid #1565c0' } }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {kpi.name || kpi.label || kpi.code}
      </Typography>
      <Chip
        label={kpi.code}
        size="small"
        clickable={codeClickable}
        onClick={codeClickable ? () => onCodeClick!(kpi.code) : undefined}
        sx={{
          fontFamily: 'monospace',
          bgcolor: '#e3f2fd',
          color: '#1565c0',
          mb: 1.5,
          cursor: codeClickable ? 'pointer' : 'default',
          textDecoration: codeClickable ? 'underline dotted' : 'none',
          '&:hover': codeClickable ? { bgcolor: '#bbdefb' } : undefined,
        }}
      />
      {codeClickable && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
          {t('kpiDashboard.popover.clickCodeHint')}
        </Typography>
      )}
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{t('kpiDashboard.popover.value')}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {displayValue}{kpi.unit ? ` ${kpi.unit}` : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{t('kpiDashboard.popover.status')}</Typography>
        <Chip
          size="small"
          label={statusLabel}
          color={STATUS_COLOR[kpi.status] || 'default'}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{t('kpiDashboard.popover.esrsStandard')}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {kpi.esrsStandard || '—'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{t('kpiDashboard.popover.campaign')}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {campaignName}
        </Typography>
      </Box>
      <Button fullWidth variant="contained" onClick={onClose} sx={{ mt: 1.5 }}>
        {t('common.close')}
      </Button>
    </Popover>
  );
}
